import React, { useState, useEffect } from 'react';
import {
  Box,
  Avatar,
  IconButton,
  CircularProgress,
  Chip,
  Divider,
  Typography,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  School as SchoolIcon,
  CurrencyRupee as CurrencyRupeeIcon,
  CheckCircle as CheckCircleIcon,
  Share as ShareIcon,
  ChevronRight as ChevronRightIcon,
  AccountCircle as AccountCircleIcon,
} from '@mui/icons-material';
import api, { handleCatch } from '../api';
import colors from '../common/Colors';
import MobileHeader from '../components/MobileHeader';
import { useAuth } from '../context/AuthContext';

const ProfileScreen = () => {
  const [loading, setLoading] = useState(true);
  const [studentInfo, setStudentInfo] = useState(null);
  const [paidSummary, setPaidSummary] = useState(null);
  const [students, setStudents] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [switchingStudent, setSwitchingStudent] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  const { signIn } = useAuth();

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedId != null) {
      onSwitchStudent();
    }
  }, [selectedId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('userToken');
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const [studentProfileRes, studentsListRes, feeSummaryRes] = await Promise.allSettled([
        api.get('api/master/student/get-student-profile', { headers }),
        api.get('api/token/get-students-list-to-switch', { headers }),
        api.get('api/school/school-transaction/get-fee-summary-student-app', { headers }),
      ]);

      //console.log('ProfileScreen student info loaded:', studentProfileRes.data);
      //console.log('ProfileScreen students list loaded:', studentsListRes.data);
      console.log('studentsListRes to switch loaded:', studentsListRes.status === 'fulfilled' ? studentsListRes.value.data : null);

      if (studentProfileRes.status === 'fulfilled') {
        setStudentInfo(studentProfileRes.value.data);
      }
      
      if (studentsListRes.status === 'fulfilled') {
        setStudents(studentsListRes.value.data || []);
      }
      
      if (feeSummaryRes.status === 'fulfilled') {
        setPaidSummary(feeSummaryRes.value.data);
      } else {
        console.warn('Fee summary failed:', feeSummaryRes.reason);
        setPaidSummary({ totalDues: 0, lastPaid: 0 }); // Set defaults
      }
    } catch (error) {
      console.error('ProfileScreen loadData error:', error);
      handleCatch(error);
      setNotification({
        open: true,
        message: 'Failed to load profile data',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const onSwitchStudent = async () => {
    try {
      setSwitchingStudent(true);
      const token = localStorage.getItem('userToken');

      const response = await api.post(
        'api/TOKEN/switch-account-client',
        { studentId: selectedId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('ProfileScreen student switched successfully:', response.data);

      signIn(response.data.username, response.data.token);
      await loadData();

      setNotification({
        open: true,
        message: 'Student account switched successfully!',
        severity: 'success',
      });
    } catch (error) {
      console.error('ProfileScreen onSwitchStudent error:', error);
      handleCatch(error);
      setNotification({
        open: true,
        message: 'Failed to switch student account',
        severity: 'error',
      });
    } finally {
      setSwitchingStudent(false);
      setSelectedId(null);
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: 'Edutron School App',
      text: 'Dear Parent/Student,\n\nDiscover the power of seamless school management with our new mobile app! Download now to stay connected with your educational journey.\n\n📱 Features:\n• Real-time notifications\n• Assignment tracking\n• Fee management\n• Online classes\n• And much more!\n\nDownload link: https://play.google.com/store/apps/details?id=com.patronservices.school',
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: Copy to clipboard
        await navigator.clipboard.writeText(shareData.text);
        setNotification({
          open: true,
          message: 'Share text copied to clipboard!',
          severity: 'success',
        });
      }
    } catch (error) {
      console.log('Share error:', error);
    }
  };

  const onRefresh = async () => {
    await loadData();
  };

  // ─── Info row helper ────────────────────────────────────────────────────────
  const InfoRow = ({ icon, label, value, last }) =>
    value ? (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.25, gap: 1.5 }}>
          <Box sx={{ color: '#888', display: 'flex', flexShrink: 0 }}>{icon}</Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 11, color: '#aaa', lineHeight: 1.2 }}>{label}</Typography>
            <Typography sx={{ fontSize: 14, color: '#222', fontWeight: 500 }}>{value}</Typography>
          </Box>
        </Box>
        {!last && <Divider sx={{ mx: 2 }} />}
      </>
    ) : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <MobileHeader
        title="Profile"
        showBack={true}
        rightAction={
          <IconButton onClick={onRefresh} disabled={loading} sx={{ color: '#fff' }}>
            <RefreshIcon />
          </IconButton>
        }
      />

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          backgroundColor: '#F5F7FA',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* ── Gradient banner ── */}
            <Box
              sx={{
                background: `linear-gradient(135deg, ${colors.BLUE} 0%, #1976D2 100%)`,
                px: 2,
                pt: 3,
                pb: 2.5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box sx={{ position: 'relative' }}>
                <Avatar
                  src={
                    studentInfo?.photo
                      ? `data:image/gif;base64,${studentInfo.photo}`
                      : undefined
                  }
                  sx={{ width: 72, height: 72, border: '3px solid rgba(255,255,255,0.4)' }}
                >
                  {!studentInfo?.photo && <SchoolIcon sx={{ fontSize: 36 }} />}
                </Avatar>
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 3,
                    right: 3,
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    bgcolor: '#4CAF50',
                    border: '2px solid #fff',
                  }}
                />
              </Box>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>
                {studentInfo?.studentName || 'Student Name'}
              </Typography>
              <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.8)' }}>
                {[studentInfo?.class, studentInfo?.section ? `- ${studentInfo.section}` : ''].filter(Boolean).join(' ')}
                {studentInfo?.rollNo ? `  •  Roll ${studentInfo.rollNo}` : ''}
              </Typography>
              <Chip
                icon={<SchoolIcon sx={{ fontSize: 13, color: `${colors.BLUE} !important` }} />}
                label="Active Student"
                size="small"
                sx={{ bgcolor: '#fff', color: colors.BLUE, fontWeight: 600, fontSize: 11 }}
              />
            </Box>

            {/* ── Contact info rows ── */}
            <Box sx={{ backgroundColor: '#fff', mt: 1, mx: 1, borderRadius: 2, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <Typography sx={{ px: 2, pt: 1.5, pb: 0.5, fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Contact Information
              </Typography>
              <InfoRow icon={<PhoneIcon sx={{ fontSize: 18 }} />} label="Mobile" value={studentInfo?.mobile ? `+91-${studentInfo.mobile}` : null} />
              <InfoRow icon={<EmailIcon sx={{ fontSize: 18 }} />} label="Email" value={studentInfo?.email} />
              <InfoRow icon={<LocationIcon sx={{ fontSize: 18 }} />} label="Address" value={[studentInfo?.address1, studentInfo?.address2].filter(Boolean).join(', ')} last />
            </Box>

            {/* ── Fee summary ── */}
            <Box sx={{ backgroundColor: '#fff', mt: 1, mx: 1, borderRadius: 2, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <Typography sx={{ px: 2, pt: 1.5, pb: 1, fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Financial Summary
              </Typography>
              <Box sx={{ display: 'flex', borderTop: '1px solid #f0f0f0' }}>
                <Box sx={{ flex: 1, textAlign: 'center', py: 2, borderRight: '1px solid #f0f0f0' }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#FFEBEE', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 0.75 }}>
                    <CurrencyRupeeIcon sx={{ color: '#F44336', fontSize: 20 }} />
                  </Box>
                  <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#C62828' }}>
                    ₹{paidSummary?.totalDues ?? 0}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#888' }}>Total Dues</Typography>
                </Box>
                <Box sx={{ flex: 1, textAlign: 'center', py: 2 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#E8F5E9', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 0.75 }}>
                    <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 20 }} />
                  </Box>
                  <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#2E7D32' }}>
                    ₹{paidSummary?.lastPaid ?? 0}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#888' }}>Last Paid</Typography>
                </Box>
              </Box>
            </Box>

            {/* ── Sibling switcher ── */}
            {students && students.length > 0 && (
              <Box sx={{ backgroundColor: '#fff', mt: 1, mx: 1, borderRadius: 2, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                <Typography sx={{ px: 2, pt: 1.5, pb: 1, fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Switch Student
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    px: 2,
                    pb: 2,
                    gap: 1.5,
                    '&::-webkit-scrollbar': { display: 'none' },
                  }}
                >
                  {students.map((item) => {
                    const isSelected = selectedId === item.studentid;
                    return (
                      <Box
                        key={item.studentid}
                        onClick={() => !switchingStudent && setSelectedId(item.studentid)}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 0.75,
                          cursor: 'pointer',
                          flexShrink: 0,
                          minWidth: 72,
                        }}
                      >
                        <Box sx={{ position: 'relative' }}>
                          <Avatar
                            src={item.photoUrl}
                            sx={{
                              width: 52,
                              height: 52,
                              bgcolor: isSelected ? colors.BLUE : '#e0e0e0',
                              color: isSelected ? '#fff' : '#666',
                              border: isSelected ? `2px solid ${colors.BLUE}` : '2px solid transparent',
                            }}
                          >
                            {switchingStudent && isSelected ? (
                              <CircularProgress size={22} sx={{ color: '#fff' }} />
                            ) : (
                              <AccountCircleIcon sx={{ fontSize: 28 }} />
                            )}
                          </Avatar>
                          {isSelected && !switchingStudent && (
                            <CheckCircleIcon sx={{ fontSize: 18, color: '#4CAF50', position: 'absolute', bottom: -2, right: -2, bgcolor: '#fff', borderRadius: '50%' }} />
                          )}
                        </Box>
                        <Typography sx={{ fontSize: 11, fontWeight: isSelected ? 700 : 400, color: isSelected ? colors.BLUE : '#555', textAlign: 'center', lineHeight: 1.2, maxWidth: 68 }} noWrap>
                          {item.studentName?.split(' ')[0]}
                        </Typography>
                        {item.class && (
                          <Typography sx={{ fontSize: 9, color: '#666', textAlign: 'center' }}>
                            {item.class}{item.section ? `-${item.section}` : ''}{item.rollNo ? ` • ${item.rollNo}` : ''}
                          </Typography>
                        )}
                        <Box sx={{ 
                          bgcolor: '#f0f0f0', 
                          borderRadius: '8px', 
                          px: 0.75, 
                          py: 0.25, 
                          mt: 0.25 
                        }}>
                          <Typography sx={{ fontSize: 7, color: '#999', textAlign: 'center', letterSpacing: 0.3 }}>
                            #{item.tenantId}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* ── Share row ── */}
            <Box
              sx={{ backgroundColor: '#fff', mt: 1, mx: 1, borderRadius: 2, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', cursor: 'pointer', '&:active': { bgcolor: '#f5f5f5' } }}
              onClick={handleShare}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.75, gap: 1.5 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: colors.BLUE, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <ShareIcon sx={{ color: '#fff', fontSize: 20 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#222' }}>Tell Your Friends</Typography>
                  <Typography sx={{ fontSize: 12, color: '#888' }}>Share the Edutron School app</Typography>
                </Box>
                <ChevronRightIcon sx={{ color: '#ccc' }} />
              </Box>
            </Box>

            <Box sx={{ height: 24 }} />
          </>
        )}
      </Box>

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfileScreen;
